import { useEffect, useRef, useState } from "react";
import { useResolutionParcels } from "../hooks";
import { apiErr } from "../utils";
import {
  ITEM_OUTCOME_OPTIONS,
  type CaseCloseValues,
  type ItemOutcome,
  type ProceedingScope,
} from "../types";

/** Stan wspólnego środka okien zamykających sprawę (rozstrzygnięcie, działki, notatka). */
export function useCaseOutcome(initialOutcome = "positive") {
  const [outcome, setOutcome] = useState(initialOutcome);
  const [parcels, setParcels] = useState<Set<string>>(new Set());
  const [note, setNote] = useState("");
  const partial = outcome === "partial";
  return {
    outcome, setOutcome, parcels, setParcels, note, setNote,
    /** Częściowe bez żadnej działki uzgodnionej byłoby negatywnym — blokuje zatwierdzenie. */
    missingParcels: partial && parcels.size === 0,
    values: (): CaseCloseValues => ({
      outcome,
      resolved_parcels: partial && parcels.size > 0 ? [...parcels] : undefined,
      note: note.trim() || undefined,
    }),
  };
}

export type CaseOutcomeState = ReturnType<typeof useCaseOutcome>;

// Co zamknięcie zrobi z działkami — zależy od zakresu sprawy (patrz ProceedingService.close).
const PARTIAL_EFFECT: Partial<Record<ProceedingScope, string>> = {
  office: "Właściciele zaznaczonych działek w sprawie dostaną tytuł „Pozyskany” (decyzja); pozostałe działki zostają do pozyskania.",
  owner: "Zamknięcie zapisze, które działki są uzgodnione. Tytuły WPW nadaje zatwierdzenie zgłoszeń w zleceniu.",
};
const PARTIAL_EFFECT_DEFAULT = "Zamknięcie zapisze, które działki są uzgodnione — status działek się nie zmienia.";

/**
 * Wspólny środek okien zamykających sprawę z rozstrzygnięciem: „Zamknij sprawę”
 * (Korespondencja), „Zakończ” wniosku i podpięcie zakończonego pakietu — rozstrzygnięcie,
 * przy CZĘŚCIOWYM lista działek uzgodnionych (sprawa WPW zaznacza je sama z danych
 * zlecenia), notatka zamknięcia. `children` trafia między rozstrzygnięcie a notatkę
 * (np. dane klauzuli ostateczności).
 */
export function CaseOutcomeFields({
  state,
  proceedingId,
  scope,
  outcomeOptions = ITEM_OUTCOME_OPTIONS,
  requireChoice = false,
  noteLabel = "Notatka zamknięcia",
  notePlaceholder = "np. warunek postawiony w decyzji (widoczna na liście spraw i na mapie)",
  children,
}: {
  state: CaseOutcomeState;
  proceedingId: string | null | undefined;
  scope: ProceedingScope | undefined;
  outcomeOptions?: { value: ItemOutcome; label: string }[];
  /** Bez domyślnego wyniku — pierwsza pozycja „— wybierz —”. */
  requireChoice?: boolean;
  noteLabel?: string;
  notePlaceholder?: string;
  children?: React.ReactNode;
}) {
  return (
    <>
      <label className="block sm:max-w-xs">
        <span className="section-label">Rozstrzygnięcie</span>
        <select className="input-base mt-1" value={state.outcome} onChange={(e) => state.setOutcome(e.target.value)}>
          {requireChoice && <option value="" disabled>— wybierz —</option>}
          {outcomeOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
      {state.outcome === "partial" && proceedingId && (
        <ParcelResolutionPicker
          proceedingId={proceedingId}
          scope={scope}
          selected={state.parcels}
          setSelected={state.setParcels}
        />
      )}
      {children}
      <label className="block">
        <span className="section-label">{noteLabel}</span>
        <textarea
          className="input-base mt-1 w-full"
          rows={2}
          placeholder={notePlaceholder}
          value={state.note}
          onChange={(e) => state.setNote(e.target.value)}
        />
      </label>
    </>
  );
}

/** Lista działek sprawy do zaznaczenia jako uzgodnione (rozstrzygnięcie częściowe). */
export function ParcelResolutionPicker({
  proceedingId,
  scope,
  selected,
  setSelected,
}: {
  proceedingId: string | null | undefined;
  scope: ProceedingScope | undefined;
  selected: Set<string>;
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>;
}) {
  const q = useResolutionParcels(proceedingId);
  const parcels = q.data?.parcels ?? [];
  const source = q.data?.suggestion_source ?? null;
  // Podpowiedź (WPW) albo zapis poprzedniego zamknięcia zaznacza się RAZ, po wczytaniu, i
  // tylko pustą listę — dalej decyduje człowiek (ponowne pobranie ani powrót do
  // „Częściowe” po zmianie wyniku nie nadpisują jego zaznaczeń).
  const seeded = useRef(false);
  useEffect(() => {
    if (!q.data || seeded.current) return;
    seeded.current = true;
    const initial = q.data.parcels.filter((p) => p.suggested || p.selected).map((p) => p.id);
    if (initial.length > 0) setSelected((prev) => (prev.size > 0 ? prev : new Set(initial)));
  }, [q.data, setSelected]);

  const allSelected = parcels.length > 0 && parcels.every((p) => selected.has(p.id));
  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <div className="space-y-1">
      <div className="flex items-start justify-between gap-2">
        <span className="section-label">
          Działki uzgodnione{parcels.length > 0 && ` (${parcels.filter((p) => selected.has(p.id)).length} z ${parcels.length})`}
        </span>
        {parcels.length > 0 && (
          <button
            type="button"
            className="section-label normal-case tracking-normal text-[var(--color-primary)] hover:underline shrink-0"
            onClick={() => setSelected(allSelected ? new Set() : new Set(parcels.map((p) => p.id)))}
          >
            {allSelected ? "Odznacz wszystkie" : "Zaznacz wszystkie"}
          </button>
        )}
      </div>
      {source && (
        <p className="text-[11px] text-[var(--color-text-secondary)]">
          Zaznaczone według danych: {source.label} — każdy właściciel działki ma tytuł albo
          zgłoszoną zgodę. Sprawdź przed zamknięciem.
        </p>
      )}
      <div className="max-h-48 overflow-y-auto border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 space-y-0.5">
        {q.isLoading && <p className="text-xs text-[var(--color-text-muted)]">Wczytywanie działek…</p>}
        {q.error && <p className="text-xs text-red-600">{apiErr(q.error)}</p>}
        {q.data && parcels.length === 0 && (
          <p className="text-xs text-[var(--color-text-muted)]">Sprawa nie ma działek.</p>
        )}
        {parcels.map((p) => (
          <label
            key={p.id}
            className="flex items-start gap-1.5 text-xs cursor-pointer px-1 py-0.5 hover:bg-[var(--color-bg-elevated)]"
          >
            <input type="checkbox" className="mt-0.5" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
            <span className="min-w-0">
              <span className="font-mono">{p.teryt}</span>
              {p.obreb && <span className="text-[var(--color-text-muted)]"> · {p.obreb}</span>}
              {p.owners.length > 0 && (
                <span className="block text-[11px] text-[var(--color-text-secondary)]">
                  {p.owners.map((o, i) => (
                    <span key={o.id} title={o.reason || undefined}>
                      {i > 0 && ", "}
                      {o.full_name}
                      {o.agreed === true && " ✓"}
                    </span>
                  ))}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
      <p className="text-[11px] text-[var(--color-text-muted)]">
        {(scope && PARTIAL_EFFECT[scope]) ?? PARTIAL_EFFECT_DEFAULT}
      </p>
      {q.data && parcels.length > 0 && selected.size === 0 && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400">
          Zaznacz co najmniej jedną działkę uzgodnioną — bez żadnej to rozstrzygnięcie negatywne.
        </p>
      )}
    </div>
  );
}
